import type { PendingOnboardingReason, PendingOnboardingResponse } from '@/shared/api';

interface ReasonPresentation {
  label: string;
  /** Was der Admin tun muss — leer, wenn nichts zu tun ist. */
  hint: string;
  /** Wahr, wenn es ein Fehler ist und kein erklärbarer Wartezustand. */
  alarm: boolean;
}

/**
 * Ein Wartezustand ist kein Fehler. Nur `ready` verlangt Handeln: dort wäre alles beisammen und
 * der Zugang fehlt trotzdem — die übrigen Zeilen erklären sich selbst und sollen nicht als Alarm
 * daherkommen, sonst sieht niemand mehr den einen, der zählt.
 */
export const REASON_PRESENTATION: Record<PendingOnboardingReason, ReasonPresentation> = {
  reissue_required: {
    label: 'Neuer Zugang erforderlich',
    hint: 'Der bisherige Zugang ist abgelaufen oder widerrufen.',
    alarm: true,
  },
  delivery_delivered: {
    label: 'Annahme bestätigt',
    hint: 'Der Anbieter hat die Nachricht angenommen. Das ist kein Zustellbeleg an den Gast.',
    alarm: false,
  },
  delivery_discarded: {
    label: 'Zustellung verworfen',
    hint: 'Die Zustellung ist nicht mehr ausstehend.',
    alarm: false,
  },
  delivery_failed: {
    label: 'Zustellung braucht Hilfe',
    hint: 'Der Zugang besteht. Prüfen Sie den Zustellzustand darunter.',
    alarm: true,
  },
  delivery_pending: {
    label: 'Zustellung geplant',
    hint: 'Der nächste Versuch läuft automatisch.',
    alarm: false,
  },
  delivery_outcome_unknown: {
    label: 'Versand unklar',
    hint: 'Der Anbieter könnte die Nachricht angenommen haben. Sie wird nicht automatisch erneut gesendet. Eine Neuausstellung ist bis zur Klärung gesperrt.',
    alarm: true,
  },
  delivery_dispatching: {
    label: 'Versand läuft',
    hint: 'Der Versandversuch ist gestartet. Sein Ergebnis steht noch aus.',
    alarm: false,
  },
  // Eine wartende Sammelzeile ist weder verworfen noch fehlgeschlagen — sie wurde früher als
  // „verworfen" gemeldet, und das war eine falsche Aussage.
  delivery_waiting_for_siblings: {
    label: 'Wartet auf die anderen Apartments',
    hint: 'Die gemeinsame Nachricht der Reservierung geht erst raus, wenn jedes Apartment seinen Zugang hat.',
    alarm: false,
  },
  ready: {
    label: 'Bereit — aber nicht verschickt',
    hint: 'Alles beisammen, der Zugang fehlt trotzdem. Hier stimmt etwas nicht.',
    alarm: true,
  },
  awaiting_delay: {
    label: 'Wartet auf den Versandtakt',
    hint: 'Die Buchung ist jünger als die eingestellte Wartezeit. Der Versand kommt gleich.',
    alarm: false,
  },
  guest_profile_missing: {
    label: 'Kein Gastprofil',
    hint: 'Ohne Namen legt kein Eingangspfad ein Profil an — Buchung prüfen.',
    alarm: false,
  },
  guest_email_missing: {
    label: 'Gastprofil ohne E-Mail',
    hint: 'Ohne Adresse hat die Einladung keinen Empfänger. E-Mail am Gast nachtragen.',
    alarm: false,
  },
  awaiting_payment: {
    label: 'Wartet auf Zahlung',
    hint: 'Manuelle Buchung ohne Zahlungseingang — sie wird bewusst zurückgehalten.',
    alarm: false,
  },
};

/** Der Alarmzustand zuerst, danach nach Anreise — was zuerst ankommt, drängt am meisten. */
export function sortPendingOnboardings(
  rows: PendingOnboardingResponse[],
): PendingOnboardingResponse[] {
  return [...rows].sort((left, right) => {
    const leftAlarm = REASON_PRESENTATION[left.reason]?.alarm ? 0 : 1;
    const rightAlarm = REASON_PRESENTATION[right.reason]?.alarm ? 0 : 1;
    return leftAlarm - rightAlarm || left.checkInDate.localeCompare(right.checkInDate);
  });
}

export const countAlarming = (rows: PendingOnboardingResponse[]): number =>
  rows.filter((row) => REASON_PRESENTATION[row.reason]?.alarm).length;
